import React, {useEffect, useState} from "react";
import Modal from "../../../BrandSignup/Modal"

import {useMutation} from "@apollo/client";
import styles from "../../../TalentApplication/styles.module.css";
import { useForm} from "react-hook-form";
import { SEND_NEW_EMAIL_VERIFY_CODE, CHECK_NEW_EMAIL_VERIFY_CODE } from "../../WithUserData/queries";


const UpdateEmailModal = ({ showEmailModal, setShowEmailModal, oldEmail, onSuccess, recaptchaRef }) => {

    const [sendNewEmailVerifyCode] = useMutation(SEND_NEW_EMAIL_VERIFY_CODE)
    const [checkNewEmailVerifyCode] = useMutation(CHECK_NEW_EMAIL_VERIFY_CODE)
    const [newEmail, setNewEmail] = useState();
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState();
    const [verifyError, setVerifyError] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
        setError,
        clearErrors,
        reset,
        trigger
    } = useForm({
        defaultValues: {
            newEmail: oldEmail
        }
    })

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleEmailVerification = async (recaptchaToken) => {
        const email = getValues("newEmail");
        try {
            await sendNewEmailVerificationCode(recaptchaToken, email);
            setCodeSent(true);
            setNewEmail(email);
        } catch (err) {
            setError("newEmail", {
                type: "manual",
                message: err.message,
            });
        }
    }
    const sendNewEmailVerificationCode = async (recaptchaToken, newEmail) => {
        setResendCooldown(30);
        await sendNewEmailVerifyCode({
            variables: {
                recaptchaToken,
                newEmail
            }
        });
    }

    const closeModal = () => {
        reset()
        clearErrors();
        setCodeSent(false);
        setShowEmailModal(false);
    }

    const handleCodeInput = async (val) => {
        const onlyNumbers = val.replace(/\D/g, "");
        setCode(onlyNumbers);

        if (onlyNumbers.length === 0) {
            setVerifyError(false);
            return;
        }

        if (onlyNumbers.length === 3) {
            try {
                let recaptchaToken = null;
                if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
                    recaptchaToken = await recaptchaRef.current.executeAsync();
                    recaptchaRef.current.reset();
                }
                const { data } = await checkNewEmailVerifyCode({
                    variables: {
                        code: onlyNumbers,
                        recaptchaToken
                    }
                });

                if (data?.checkNewEmailVerifyCode) {
                    setShowEmailModal(false);
                    setVerifyError(false);
                    onSuccess();
                } else {
                    setVerifyError(true);
                }
            } catch {
                setVerifyError(true);
            }
        }
    };


    return (
        <Modal isOpenModal={showEmailModal} closeModal={closeModal} light={true}>
            {!codeSent && <form
                onSubmit={async (e) => {
                    e.preventDefault();
                    const valid = await trigger();
                    if (!valid) return;
                    let token = null;
                    if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
                        token = await recaptchaRef.current.executeAsync();
                        recaptchaRef.current.reset();
                    }
                    handleSubmit(() => handleEmailVerification(token))();
                }}
                className="space-y-4 w-full max-w-sm px-4 pb-6"
            >
                <div className="mb-3">
                    <label className="block mb-4 text-black">New email address:</label>
                    <div className={`flex w-full rounded border ${errors.newEmail ? "border-red-500" : "border-gray-900"}`}>
                        <input
                            {...register("newEmail", {
                                required: "New email is required",
                                pattern: {
                                    value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                                    message: "Invalid email",
                                },
                                validate: {
                                    notSameAsOld: (value) =>
                                        value !== oldEmail || "New email must be different from current email",
                                },
                            })}
                            onChange={() => {
                                clearErrors("newEmail");
                            }}
                            className="w-full rounded outline-none p-2 bg-gray-300 text-black hover:bg-gray-400"
                            style={{
                                borderColor: errors.newEmail ? "#e53935" : "#404040"
                            }}
                        />
                    </div>
                    {errors.newEmail && <p className="text-red-500 text-sm">{errors.newEmail.message}</p>}
                </div>

                <div className="flex justify-content-end mt-16">
                    <button
                        type="button"
                        className="cursor-pointer w-24 py-1 bg-gray-300 text-black rounded-xl flex items-center justify-center mx-auto hover:bg-gray-400"
                        onClick={closeModal}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={`w-24 py-1 bg-gray-300 rounded-xl flex items-center justify-center mx-auto cursor-pointer text-black  hover:bg-gray-400`}
                    >
                        Verify
                    </button>
                </div>
            </form>}
            {newEmail && codeSent && <>
                <p className='text-black'>
                    We sent an email to {newEmail} <br/>Please enter the verification code below:
                </p>

                <div className="mt-4">
                    <input
                        maxLength={3}
                        type="text"
                        inputMode="numeric"
                        value={code}
                        onChange={(e) => handleCodeInput(e.target.value)}
                        className="w-24 text-center text-black bg-gray-300 text-xl mt-2 p-2 rounded border bg-[#1A1A1A] border-white"
                    />
                    {verifyError && <p className="text-red-500 mt-2">Invalid code, please try again.</p>}
                </div>
                <div className="flex justify-content-end mt-8">
                    <button
                        onClick={async () => {
                            let token = null;
                            if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
                                token = await recaptchaRef.current.executeAsync();
                                recaptchaRef.current.reset();
                            }
                            await sendNewEmailVerificationCode(token, newEmail);
                        }}
                        disabled={resendCooldown > 0}
                        className={`px-3 py-1 rounded-xl flex items-center justify-center mx-auto ${resendCooldown > 0 ? 'bg-gray-200 cursor-not-allowed text-gray-500 ' : 'bg-gray-300 cursor-pointer text-black hover:bg-gray-400'} `}
                    >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown} seconds` : "Resend verification code"}
                    </button>
                </div>
            </>}
        </Modal>
    )
}

export default UpdateEmailModal;